import { Directive, inject, Input } from "@angular/core";
import { of } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDownloadDialogComponent } from "../../../dialog-components/confirm-download/confirm-download-dialog";
import { IDownloadImageDialogData } from "../../interfaces/i-download-image-dialog-data";
import { ToastService } from "../../services/internal-services/toast.service";
import { INTERNAL_TOAST_TYPES, TOAST_STATUS } from "../../model/ToastConstants";
import { IVisionToast } from "../../interfaces/i-vision-toast";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";

@Directive()
export abstract class DownloadImageryComponent {
  viewerService = inject(VIEWER_SERVICE);

  showToolbar = false;

  imageSelected: IMetadataImage;

  protected constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private mapService: MapService,
  ) {}

  @Input()
  set selectedImage(img: IMetadataImage) {
    this.imageSelected = img;
  }

  handleOpenDialog() {
    const initConfig = this.mapService.getInitConfig();
    if (initConfig.imageExports === 0) {
      this.showDialogBuyExports(initConfig.imageExports);
    } else {
      this.showDialog(initConfig.imageExports);
    }
  }

  showDialogBuyExports(exports: number): void {
    const dialogData: IDownloadImageDialogData = {
      exports,
      image: null,
    };
    const dialogRef = this.dialog.open(ConfirmDownloadDialogComponent, {
      width: "350px",
      data: dialogData,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const notification: IVisionToast = {
          id: `download-image-toast`,
          message: "Contact with the sales department",
          closeButton: true,
          type: INTERNAL_TOAST_TYPES.DOWNLOAD_NOTIFICATIONS,
        };
        this.toastService.setNotification(notification);
      }
    });
  }

  showDialog(exports: number): void {
    const dialogData: IDownloadImageDialogData = {
      image: this.imageSelected,
      exports,
    };
    const dialogRef = this.dialog.open(ConfirmDownloadDialogComponent, {
      width: "350px",
      data: dialogData,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const notification: IVisionToast = {
          id: `download-image-toast`,
          message: "Downloading images...",
          type: INTERNAL_TOAST_TYPES.DOWNLOAD_NOTIFICATIONS,
          timeout: 120000,
          closeButton: false,
          progressBar: true,
        };
        this.toastService.setNotification(notification);
        this.googleAnalyticsService.setGoogleAnalyticsEvent(
          GoogleAnalyticsConstantsEvents.DOWNLOAD_IMAGE,
          GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
          true,
        );
        const coordinates = this.viewerService.getCenterCoordinates();
        this.mapService
          .extractImages(
            this.imageSelected.layername,
            0,
            coordinates,
            null,
            "best",
            "csv",
            null,
            null,
            null,
            null,
            this.imageSelected.child_aoi,
            false,
          )
          .subscribe({
            next: (blob) => {
              try {
                const blobUrl = window.URL.createObjectURL(blob);
                this.forceDownload(blobUrl, `image_download_${this.imageSelected.child_aoi}`);
                this.toastService.removeNotificationsById(`download-image-toast`);
              } catch (err) {
                throw new Error();
              }
            },
            error: (err) => {
              this.toastService.removeNotificationsById(`download-image-toast`);
              const error: IVisionToast = {
                id: `download-imagery-warning`,
                message: "Can't download images in this location. Please try again later.",
                type: TOAST_STATUS.WARNING,
                timeout: 6000,
                closeButton: false,
                progressBar: false,
              };
              this.toastService.setNotification(error);
              return of(err);
            },
          });
      }
    });
  }

  forceDownload(blob, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = blob;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}
